import { useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getRedirectResult,
  signInWithRedirect,
  signInWithCredential,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { auth } from "@/config/firebase";
import { storePostAuthRoute } from "@/lib/authRedirect";
import { authErrorMessage, isCancelledAuthRequest } from "@/lib/firebaseAuthErrors";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let authReady = false;
    let redirectReady = false;
    const finishLoading = () => {
      if (!cancelled && authReady && redirectReady) setLoading(false);
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (cancelled) return;
      setUser(firebaseUser);
      authReady = true;
      finishLoading();
    });

    void getRedirectResult(auth).then((result) => {
      if (cancelled || !result?.user) return;
      setUser(result.user);
      setError(null);
    }).catch((err) => {
      if (cancelled) return;
      if (isCancelledAuthRequest(err)) {
        setError(null);
        return;
      }
      setError(authErrorMessage(err, "Google sign in failed"));
    }).finally(() => {
      redirectReady = true;
      finishLoading();
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const signInWithEmail = useCallback(async (email: string, password: string): Promise<User> => {
    try {
      setError(null);
      setLoading(true);
      const credential = await signInWithEmailAndPassword(auth, email, password);
      return credential.user;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign in failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signUpWithEmail = useCallback(async (email: string, password: string, displayName: string): Promise<User> => {
    try {
      setError(null);
      setLoading(true);
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(credential.user, { displayName });
      return credential.user;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign up failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async (postAuthRoute?: string): Promise<User | null> => {
    let redirectStarted = false;
    try {
      setError(null);
      setLoading(true);

      if (Capacitor.isNativePlatform()) {
        // Native: use Capacitor Firebase Auth plugin for proper OAuth flow
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        const result = await FirebaseAuthentication.signInWithGoogle();
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error("No ID token from Google Sign-In");
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await signInWithCredential(auth, credential);
        return userCredential.user;
      } else {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        storePostAuthRoute(postAuthRoute);
        redirectStarted = true;
        await signInWithRedirect(auth, provider);
        return null;
      }
    } catch (err) {
      const message = authErrorMessage(err, "Google sign in failed");
      setError(message);
      if (isCancelledAuthRequest(err)) return null;
      throw err;
    } finally {
      if (!redirectStarted) setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) {
      const message = "Enter your email address before requesting a password reset.";
      setError(message);
      throw new Error(message);
    }
    try {
      setError(null);
      await sendPasswordResetEmail(auth, trimmed);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Password reset failed";
      setError(message);
      throw err;
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        await FirebaseAuthentication.signOut();
      }
      await firebaseSignOut(auth);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign out failed";
      setError(message);
    }
  }, []);

  return { user, loading, error, clearError, signInWithEmail, signUpWithEmail, signInWithGoogle, resetPassword, signOut };
}
